import { createFileRoute } from "@tanstack/react-router";
import { PriorityServiceAreaPage } from "@/components/PriorityServiceAreaPage";
import { seoHead, serviceSchema, localBusinessSchema } from "@/lib/seo";
import { faqSchema } from "@/components/FAQAccordion";

const FAQS = [
  { q: "What does suspension repair cost in Poplar?", a: "Drop links from £75 fitted, shock absorbers from £180 per pair, full strut assemblies vary by model. We'll inspect for free and quote clearly." },
  { q: "How do I know my suspension is worn?", a: "Knocking over bumps, the car bouncing more than once after a speed hump, uneven tyre wear, or pulling to one side. We'll do a free check." },
  { q: "Do you do wheel alignment after suspension work?", a: "Always recommended after major suspension work. We can carry out 4-wheel alignment or refer to a specialist if your car needs Hunter Hawkeye alignment." },
  { q: "Will my car fail the MOT on suspension?", a: "Excessive play, knocks, leaking shocks or perished bushes can fail an MOT. Fixing them early avoids a fail and keeps the ride safe." },
];

export const Route = createFileRoute("/poplar/suspension-repairs")({
  head: () => seoHead({
    path: "/poplar/suspension-repairs",
    title: "Suspension Repair in Poplar, E14 — Shocks, Springs, Bushes | Woodseer Autos",
    description: "Suspension repair in Poplar, E14. Shocks, springs, drop links, wishbones and bushes. Free check and clear quote.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Poplar", path: "/areas/poplar" },
      { name: "Suspension Repairs", path: "/poplar/suspension-repairs" },
    ],
    jsonLd: [
      localBusinessSchema({ serviceCoverage: ["Poplar"] }),
      serviceSchema({
        name: "Suspension Repair in Poplar",
        serviceType: "Suspension repair",
        description: "Shock absorbers, springs, drop links, wishbones and bush replacement for drivers in Poplar, E14.",
        areaServed: "Poplar, London",
        url: "https://woodseerautos.co.uk/poplar/suspension-repairs",
      }),
      faqSchema(FAQS) as Record<string, unknown>,
    ],
  }),
  component: () => (
    <PriorityServiceAreaPage
      areaSlug="poplar"
      areaName="Poplar"
      serviceSlug="suspension-repairs"
      serviceName="Suspension Repairs"
      parentServiceSlug="mechanical-repairs"
      hero="Knocking over bumps, bouncy ride, or uneven tyre wear? Our Poplar workshop diagnoses and replaces shock absorbers, springs, drop links, wishbones and bushes."
      localBenefit="Free suspension check — we'll show you what's worn before you commit"
      whatsIncluded={[
        "Full underside inspection on the ramp",
        "Shock absorber leak test and bounce test",
        "Springs, drop links and bushes replaced",
        "Wishbones and anti-roll bar links",
        "Wheel alignment recommended after major work",
        "Road test for noise and handling",
      ]}
      commonIssues={[
        "Clunking or knocking over bumps",
        "Car continues bouncing after a speed hump",
        "Uneven tyre wear (inside or outside edge)",
        "Pulling to one side when driving",
        "Steering feels loose or vague",
        "Squeaking on full lock",
      ]}
      process={[
        { step: "Inspection", desc: "Free check on the ramp — we'll show you exactly what's worn." },
        { step: "Clear quote", desc: "Parts and labour broken down by component. Decide what to do now and what can wait." },
        { step: "Fitted properly", desc: "Quality parts (KYB, Bilstein, Lemforder) and torqued to spec." },
        { step: "Alignment", desc: "Recommended after major work to keep tyre wear even." },
      ]}
      whyUs={[
        "Quality OE-grade parts as standard",
        "Free, no-obligation inspection",
        "We'll tell you what can safely wait",
        "Alignment available after major suspension work",
      ]}
      faqs={FAQS}
    />
  ),
});
